// ═══════════════════════════════════════════════════════════════
// DonorSidebarBlock.jsx
// Compact donor summary for the DonorDetailView 2-col sidebar.
// Computes giving stats from the gifts table on mount.
// Path: src/components/features/DonorSidebarBlock.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Gift, AlertCircle, TrendingUp, Pin } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { formatCurrency, resolveGivingLevel } from "../../constants/giftTypes";
import { supabase } from "../../lib/supabase";

export const DonorSidebarBlock = ({
  member,
  palette,
  onAddGift,
  canEdit,
  // Form integration — recognition override editor
  detailForm,
  updateField,
  updateMember,
  chapterSettings,
}) => {
  const [giftStats, setGiftStats] = useState(null);

  useEffect(() => {
    if (!member?.id) return;

    const load = async () => {
      try {
        // Fetch all gifts for this member
        const allGifts = [];
        const PAGE_SIZE = 1000;
        let offset = 0;
        while (true) {
          const { data, error } = await supabase
            .from("gifts")
            .select("amount, gift_date")
            .eq("member_id", member.id)
            .order("gift_date", { ascending: false })
            .range(offset, offset + PAGE_SIZE - 1);

          if (error || !data) break;
          allGifts.push(...data);
          if (data.length < PAGE_SIZE) break;
          offset += PAGE_SIZE;
        }

        if (allGifts.length === 0) {
          setGiftStats(null);
          return;
        }

        // Compute stats
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        // Fiscal year: July 1 – June 30
        const fyStart = currentMonth >= 6
          ? `${currentYear}-07-01`
          : `${currentYear - 1}-07-01`;

        let lifetime = 0;
        let currentYearTotal = 0;
        let giftCount = allGifts.length;
        let lastGiftDate = allGifts[0]?.gift_date || null;
        let lastGiftAmount = Number(allGifts[0]?.amount) || 0;

        // Track consecutive years
        const giftYears = new Set();

        allGifts.forEach((g) => {
          const amt = Number(g.amount) || 0;
          lifetime += amt;
          if (g.gift_date >= fyStart) {
            currentYearTotal += amt;
          }
          if (g.gift_date) {
            const yr = new Date(g.gift_date + "T12:00:00").getFullYear();
            giftYears.add(yr);
          }
        });

        // Consecutive years ending with current/recent
        let consecutive = 0;
        const checkYear = currentMonth >= 6 ? currentYear : currentYear - 1;
        for (let yr = checkYear; yr >= checkYear - 20; yr--) {
          if (giftYears.has(yr)) {
            consecutive++;
          } else {
            break;
          }
        }

        setGiftStats({
          lifetime,
          currentYear: currentYearTotal,
          lastGiftDate,
          lastGiftAmount,
          giftCount,
          consecutiveYears: consecutive,
        });
      } catch (err) {
        // silent
      }
    };

    load();
  }, [member?.id]);

  const hasDonorHistory = giftStats !== null || member?.isDonor;
  const hasAckPending = member?.acknowledgmentPending === true;

  // ── Recognition tier resolution ───────────────────────────────
  // Manual override (set on this form) wins over the amount-driven
  // resolveGivingLevel() result. When no override is set, fall back
  // to lifetime-giving lookup against the perpetuity scope (matches
  // the badge's traditional "donor wall" meaning).
  const givingLevels = chapterSettings?.givingLevels || [];
  const overrideName = (detailForm?.givingLevelOverride || "").trim() || null;
  const resolvedLevel =
    overrideName ||
    resolveGivingLevel(giftStats?.lifetime || 0, givingLevels, "perpetuity");
  const isPinned = !!overrideName;

  // ── Giving level badge ────────────────────────────────────────
  const GivingLevelBadge = () => {
    if (!resolvedLevel) return null;
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 10px",
          borderRadius: 999,
          background: `${palette.accent1}15`,
          border: `1px solid ${palette.accent1}30`,
          marginBottom: 10,
        }}
        title={isPinned ? "Manually pinned via Recognition Override" : "Calculated from lifetime giving"}
      >
        {isPinned ? (
          <Pin size={ICON_SIZES.xs} style={{ color: palette.accent1 }} />
        ) : (
          <TrendingUp size={ICON_SIZES.xs} style={{ color: palette.accent1 }} />
        )}
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: palette.accent1,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {resolvedLevel}
          {isPinned && (
            <span style={{ fontWeight: 500, marginLeft: 4, opacity: 0.75 }}>
              (pinned)
            </span>
          )}
        </span>
      </div>
    );
  };

  // ── Recognition override dropdown ─────────────────────────────
  const RecognitionOverrideField = () => {
    // Only show when caller wired form integration AND tiers exist
    if (!updateField || !updateMember || !member?.id) return null;
    if (!givingLevels.length) return null;

    const handleChange = (val) => {
      const next = val || null;
      updateField("givingLevelOverride", next);
      updateMember(member.id, { givingLevelOverride: next }, true);
    };

    return (
      <div style={{ marginBottom: 10 }}>
        <label
          style={{
            display: "block",
            fontSize: 10,
            fontWeight: 700,
            color: palette.text3,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginBottom: 3,
          }}
        >
          Recognition Override
        </label>
        <select
          value={detailForm?.givingLevelOverride || ""}
          onChange={(e) => handleChange(e.target.value)}
          disabled={!canEdit}
          style={{
            width: "100%",
            padding: "6px 8px",
            borderRadius: 6,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
            border: `1px solid ${palette.inputBorder}`,
            background: palette.inputBg,
            color: palette.inputColor,
            outline: "none",
            cursor: canEdit ? "pointer" : "default",
            opacity: canEdit ? 1 : 0.6,
          }}
        >
          <option value="">— Auto (from giving) —</option>
          {givingLevels.map((t) => (
            <option key={t.id || t.name} value={t.name}>
              {t.name} ({t.scope || "perpetuity"})
            </option>
          ))}
        </select>
      </div>
    );
  };

  // ── Stat row ──────────────────────────────────────────────────
  const StatRow = ({ label, value, highlight }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "4px 0",
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <span
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 600,
          color: palette.text2,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: FONT_SIZES.sm,
          fontWeight: highlight ? 700 : 500,
          color: highlight ? palette.accent1 : palette.text1,
          fontFamily: FONT_FAMILY,
        }}
      >
        {value || "—"}
      </span>
    </div>
  );

  return (
    <div>
      {/* Section label */}
      <div
        style={{
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          color: palette.text3,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Gift size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
        Giving
      </div>

      {/* Acknowledgment warning */}
      {hasAckPending && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            borderRadius: 6,
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            marginBottom: 8,
          }}
        >
          <AlertCircle size={ICON_SIZES.sm} style={{ color: "#d97706", flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "#92400e" }}>
            Acknowledgment pending
          </span>
        </div>
      )}

      {/* Giving level badge (resolved override or calculated) */}
      <GivingLevelBadge />

      {/* Recognition override dropdown */}
      <RecognitionOverrideField />

      {/* Stats */}
      {hasDonorHistory && giftStats ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <StatRow
            label="Lifetime Giving"
            value={formatCurrency(giftStats.lifetime)}
            highlight
          />
          <StatRow
            label="This Year"
            value={formatCurrency(giftStats.currentYear)}
          />
          <StatRow
            label="Last Gift"
            value={
              giftStats.lastGiftDate
                ? new Date(giftStats.lastGiftDate + "T12:00:00").toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "—"
            }
          />
          <StatRow
            label="Last Amount"
            value={formatCurrency(giftStats.lastGiftAmount)}
          />
          <StatRow
            label="Total Gifts"
            value={giftStats.giftCount > 0 ? `${giftStats.giftCount}` : "—"}
          />
          {giftStats.consecutiveYears > 0 && (
            <StatRow
              label="Consecutive Yrs"
              value={`${giftStats.consecutiveYears}`}
            />
          )}
        </div>
      ) : (
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text3,
            fontStyle: "italic",
            marginBottom: 8,
          }}
        >
          No gifts on file
        </div>
      )}

      {/* Add Gift button */}
      {canEdit && (
        <button
          onClick={onAddGift}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            width: "100%",
            marginTop: 10,
            padding: "7px 0",
            borderRadius: 7,
            border: `1px dashed ${palette.accent1}60`,
            background: `${palette.accent1}08`,
            color: palette.accent1,
            fontSize: FONT_SIZES.xs,
            fontWeight: 700,
            fontFamily: FONT_FAMILY,
            cursor: "pointer",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `${palette.accent1}15`;
            e.currentTarget.style.borderColor = palette.accent1;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = `${palette.accent1}08`;
            e.currentTarget.style.borderColor = `${palette.accent1}60`;
          }}
        >
          <Gift size={ICON_SIZES.sm} />
          Record Gift
        </button>
      )}
    </div>
  );
};
